import React, { memo } from "react";
import cns from "classnames";
import { useObserver } from "mobx-react";
import { Box, Divider, FormControlLabel, Grid, makeStyles } from "@material-ui/core";
import { Info } from "@material-ui/icons";
import locale, { getLocaleMonths } from "@bonliva-crm/locale";
import { ThemeType } from "@theme";
import {
  Body,
  SecondaryButton,
  RegularButton,
  WithTooltip,
  DepartmentSelect
} from "@components";
import { FormControls } from "@components/forms";
import { Text, Select, RadioGroup, Checkbox } from "@components/formx";
import { ApproveBox } from "../components/Common/ApproveBox";
import { useConsultantEditorContext } from "../ConsultantEditorContext";
import { CustomerType } from "@bonliva-crm/graphql";
import CustomerSelector from "@containers/VirtualSelector/CustomerSelector/CustomerSelector";
import { LegalPersonType } from "@bonliva-crm/shared";

export const WorkExperienceInfo = memo(() => {
  const editorStore = useConsultantEditorContext();
  const { consultantCVManager } = editorStore;
  const classes = useStyles({});

  return useObserver(() => {
    return (
      <>
        {editorStore.educationItems.length === 0 && (
          <Grid item>
            <Body color="textSecondary" style={{ textAlign: "center" }}>
              ({locale.EmptyHere.toLocaleLowerCase()})
            </Body>
          </Grid>
        )}

        {editorStore.workExperienceItems.map((record, i) => {
          const fieldName = `workExperience.items.${i}`;
          const customerRefValue = editorStore.form.getValue(
            `${fieldName}.customerRef`
          ) as { Name: string; Id: string; Type?: string } | null;
          const changedValueExists =
            consultantCVManager.workExperienceNotApproved(record);

          return (
            <Grid
              key={i}
              container
              spacing={1}
              className={cns(classes.item, {
                [classes.notApproved]: changedValueExists
              })}
            >
              {changedValueExists && (
                <ApproveBox
                  onCheck={() => consultantCVManager.approveWorkExperience(record)}
                />
              )}

              <Grid item xs={12}>
                <Text label={locale.WorkplaceAndDeps} name={`${fieldName}.workPlace`} />
              </Grid>

              <Grid item xs={12}>
                <CustomerSelector
                  fullWidth
                  slim
                  // open={
                  //   editorStore.form.getValue(`${fieldName}.customerRef`)?.length === 0
                  // }
                  shrink
                  label={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        margin: "0 0 8px 0"
                      }}
                    >
                      {locale.SearchableCustomer}{" "}
                      <WithTooltip
                        placement={"top"}
                        title={locale.MakeExperienceSearchable}
                      >
                        <Info />
                      </WithTooltip>
                    </div>
                  }
                  hideButton
                  onChange={(v) => {
                    const HospitalName = v?.Parents?.find(
                      (x) => x.Type === "Hospital"
                    )?.Name;
                    editorStore.setHospital(
                      fieldName,
                      v
                        ? {
                            Name: `${HospitalName ? `${HospitalName} >` : ""} ${v.Name}`,
                            Id: v._id,
                            Type: v.Type
                          }
                        : null
                    );
                  }}
                  value={
                    customerRefValue
                      ? {
                          label: customerRefValue.Name,
                          value: customerRefValue.Id
                        }
                      : null
                  }
                  initialFilter={{
                    customerType: [
                      // CustomerType.Region,
                      CustomerType.Hospital
                      // CustomerType.Department
                    ]
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <DepartmentSelect
                  disabled={!customerRefValue}
                  error={editorStore.form.getError(`${fieldName}.departmentIds`)}
                  options={editorStore.allDepartments
                    .filter((x) => x.DirectParent?.Id === customerRefValue?.Id)
                    ?.toLabelValue(
                      (x) => x.Name,
                      (x) => x._id
                    )}
                  onChange={(v) =>
                    editorStore.form.setValue(`${fieldName}.departmentIds`, v)
                  }
                  value={editorStore.form.getValue(`${fieldName}.departmentIds`)}
                />
              </Grid>

              <Grid item xs={12}>
                <Select
                  name={`${fieldName}.areaOfExpertise`}
                  options={editorStore.allAreasOfExp.map((a) => ({
                    label: a,
                    value: a
                  }))}
                  label={locale.Expertise}
                />
              </Grid>

              <Grid container item xs={12} sm={6} alignItems="flex-end" spacing={1}>
                <Grid item xs={4}>
                  <Text label={locale.FromYear} name={`${fieldName}.fromYear`} />
                </Grid>
                <Grid item xs={8}>
                  <Select
                    disabled={!record.fromYear}
                    fullWidth
                    label={locale.Month}
                    name={`${fieldName}.fromMonth`}
                    options={getLocaleMonths().toLabelValue(
                      (v) => v,
                      (v, ind) => ind.toString()
                    )}
                    sortOptions={false}
                  />
                </Grid>
              </Grid>

              <Grid container item xs={12} sm={6} alignItems="flex-end" spacing={1}>
                <Grid item xs={4}>
                  <Text label={locale.ToYear} name={`${fieldName}.toYear`} />
                </Grid>
                <Grid item xs={8}>
                  <Select
                    disabled={!record.toYear}
                    fullWidth
                    label={locale.Month}
                    name={`${fieldName}.toMonth`}
                    options={getLocaleMonths().toLabelValue(
                      (v) => v,
                      (v, ind) => ind.toString()
                    )}
                    sortOptions={false}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Text label={locale.Title} name={`${fieldName}.roles`} />
              </Grid>
              <Grid item xs={12}>
                <Text
                  label={locale.Description}
                  name={`${fieldName}.experience`}
                  TextFieldProps={{
                    multiline: true,
                    fullWidth: true
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <RadioGroup
                  name={`${fieldName}.employmentType`}
                  label={locale["Employment type"]}
                  onChange={(e, v) => editorStore.setEmploymentType(fieldName, v)}
                  options={Object.values(LegalPersonType).map((v) => ({
                    label: locale.cooperationForm[v],
                    value: v
                  }))}
                />
              </Grid>
              {editorStore.form.getValue(`${fieldName}.employmentType`) ===
                LegalPersonType.Subcontractor && (
                <Grid item xs={12}>
                  <Select
                    multi
                    name={`${fieldName}.competitors`}
                    label={locale.ConsultingCompanyLabel}
                    options={editorStore.allCompetitors.map((u) => ({
                      label: u,
                      value: u
                    }))}
                  />
                </Grid>
              )}

              <FormControls
                fitted
                right={
                  <Box>
                    <FormControlLabel
                      className={classes.excludeFromPresentation}
                      labelPlacement="start"
                      control={<Checkbox name={`${fieldName}.excludeFromPresentation`} />}
                      label={locale.excludeFromPresentation}
                    />
                    <RegularButton
                      onClick={() => {
                        editorStore.removeWorkExperience(i);
                        consultantCVManager.approveWorkExperience(record);
                      }}
                      data-test="RemoveWorkExperience"
                    >
                      {locale.Delete}
                    </RegularButton>
                  </Box>
                }
              />
              <Grid item xs={12}>
                <Divider style={{ flex: 1 }} />
              </Grid>
            </Grid>
          );
        })}

        <FormControls
          fitted
          right={
            <SecondaryButton
              onClick={() => editorStore.addWorkExperience()}
              data-test="AddWorkExperience"
              variant="text"
            >
              {locale.Add}
            </SecondaryButton>
          }
        />
      </>
    );
  });
});

const useStyles = makeStyles((theme: ThemeType) => ({
  dates: {
    ...theme.mixins.childrenMarginRight(theme.spacing()),
    display: "flex",
    alignItem: "bottom"
  },
  item: {
    "&:not(:last-child)": {
      marginBottom: theme.spacing()
    }
  },
  excludeFromPresentation: {
    width: "150px",
    marginRight: "50px"
  },
  notApproved: {
    ...theme.palette.error
  }
}));
